import { useCallback, useEffect, useRef, useState } from "react";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { X } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { STAFF_ONLY_ROLES } from "@/auth/computeProtectedRedirect";
import { FloorCreateFab } from "./FloorCreateFab";
import { PostComposer } from "./PostComposer";
import { CreateCollaborationModal } from "./CreateCollaborationModal";

// Portal-wide "Create" launcher (2026-10 staging UI pass). Mounted ONCE by the freelancer portal
// shell, so the same floating + is on every freelancer tab. It only hosts EXISTING tools:
//   • New post → the existing PostComposer (picture-first, same upload-availability notice) in a
//     dialog over the current tab — cancelling leaves the tab, scroll position and URL untouched.
//   • Start a collaboration → the existing read-only eligibility check, then the existing
//     CreateCollaborationModal (or the server's own "not eligible" message).
// After a successful post it announces `njm:floor-post-created` so an open Floor feed refreshes.
export const FLOOR_POST_CREATED_EVENT = "njm:floor-post-created";

const BLOCKING_SELECTOR = '[aria-modal="true"], [role="dialog"], [data-testid="create-collaboration-overlay"], [data-testid="mobile-sheet"]';

function useOtherBlockingUi(ownRootRef) {
  // Hide the FAB while some OTHER modal/sheet is open, or (phones) while typing with the keyboard up.
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    const check = () => {
      const own = ownRootRef.current;
      const modal = [...document.querySelectorAll(BLOCKING_SELECTOR)].some((el) => !(own && own.contains(el)));
      const a = document.activeElement;
      const typing = !!a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.tagName === "SELECT" || a.isContentEditable)
        && window.matchMedia && window.matchMedia("(max-width: 767px)").matches && !(own && own.contains(a));
      setBlocked(modal || typing);
    };
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", check);
    return () => { mo.disconnect(); document.removeEventListener("focusin", check); document.removeEventListener("focusout", check); };
  }, [ownRootRef]);
  return blocked;
}

export function PortalCreateLauncher({ resetKey }) {
  const { user } = useAuth();
  const rootRef = useRef(null);
  const blockedByOtherUi = useOtherBlockingUi(rootRef);
  const canStartCollab = Boolean(user) && !STAFF_ONLY_ROLES.includes(user.role);

  const [composerOpen, setComposerOpen] = useState(false);
  const [composerBusy, setComposerBusy] = useState(false);
  const composerBusyRef = useRef(false);
  const [members, setMembers] = useState([]);
  const [collabChecking, setCollabChecking] = useState(false);
  const [showCreateCollab, setShowCreateCollab] = useState(false);
  const [collabNotice, setCollabNotice] = useState(null);
  const [collabRetry, setCollabRetry] = useState(false);
  const collabCheckingRef = useRef(false);
  const collabRequestRef = useRef(null);
  useEffect(() => () => collabRequestRef.current?.abort(), []);
  const [roleOptions, setRoleOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const lastFocusRef = useRef(null);

  // Tab change while a tool is open: leave it open (the user chose it), but a closed menu
  // never survives a tab change — FloorCreateFab resets on `resetKey`.

  const restoreFocus = () => { const el = lastFocusRef.current; if (el && document.contains(el)) el.focus(); else rootRef.current?.querySelector('[data-testid="floor-create-fab"]')?.focus(); };

  const openComposer = useCallback(() => {
    lastFocusRef.current = document.activeElement;
    setComposerOpen(true);
    if (!members.length) api.get("/v1/community/members").then((r) => setMembers(r.data.items || [])).catch(() => {});
  }, [members.length]);
  useEffect(() => {
    window.addEventListener("njm:open-floor-composer", openComposer);
    return () => window.removeEventListener("njm:open-floor-composer", openComposer);
  }, [openComposer]);
  const closeComposer = useCallback(() => { if (composerBusyRef.current) return; setComposerOpen(false); setTimeout(restoreFocus, 0); }, []);

  const openCreateCollab = async () => {
    if (collabCheckingRef.current) return;
    collabCheckingRef.current = true;
    const request = new AbortController();
    collabRequestRef.current = request;
    lastFocusRef.current = document.activeElement;
    setCollabChecking(true);
    if (!roleOptions.length) api.get("/v1/build/roles").then((r) => setRoleOptions(r.data.items || [])).catch(() => {});
    if (!cityOptions.length) api.get("/v1/build/locations").then((r) => setCityOptions(r.data.items || [])).catch(() => {});
    let verdict = null;
    try {
      verdict = (await api.get("/v1/collaboration/member-collaboration-eligibility", { signal: request.signal })).data;
      if (request.signal.aborted) return;
      if (typeof verdict?.eligible !== "boolean") throw new Error("Invalid eligibility response");
    } catch {
      if (request.signal.aborted) return;
      setCollabNotice("We couldn't check collaboration access. Please try again.");
      setCollabRetry(true);
      return;
    } finally {
      collabCheckingRef.current = false;
      setCollabChecking(false);
    }
    setCollabRetry(false);
    setCollabNotice(null);
    if (verdict && verdict.eligible === false) {
      setCollabNotice(verdict.message || (verdict.code === "staff_cannot_create_member_collaboration"
        ? "Staff accounts can't start a Member Collaboration."
        : "Only approved, onboarded NJM Build members can start a Member Collaboration."));
      return;
    }
    setShowCreateCollab(true);
  };

  useDialogFocus(rootRef, composerOpen || !!collabNotice, composerOpen ? closeComposer : () => { collabRequestRef.current?.abort(); setCollabNotice(null); setTimeout(restoreFocus, 0); });

  if (!user) return null;
  const ownToolOpen = composerOpen || showCreateCollab || !!collabNotice;

  return (
    <div ref={rootRef} data-testid="portal-create-launcher">
      <FloorCreateFab onNewPost={openComposer} onStartCollaboration={canStartCollab ? openCreateCollab : undefined}
        busy={collabChecking} hidden={ownToolOpen || blockedByOtherUi} resetKey={resetKey} />

      {composerOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink/60 md:p-4" data-testid="portal-composer-overlay">
          <div role="dialog" aria-modal="true" aria-labelledby="portal-composer-title"
            className="w-full md:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-2xl md:rounded-2xl bg-paper shadow-xl"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="portal-composer-dialog">
            <div className="flex items-center justify-between gap-3 border-b border-hairline px-4 py-3">
              <h2 id="portal-composer-title" className="font-mont font-bold text-sm text-ink">New post on The Floor</h2>
              <button type="button" disabled={composerBusy} onClick={closeComposer} aria-label="Close new post" data-testid="portal-composer-close"
                className="h-11 w-11 -mr-2 flex items-center justify-center rounded-full text-ink-soft hover:bg-surface2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink">
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="p-3 sm:p-4">
              <PostComposer members={members} expanded pictureModeRequested
                onBusyChange={(value) => { composerBusyRef.current = value; setComposerBusy(value); }}
                onExpandedChange={(v) => { if (!v) closeComposer(); }}
                onPosted={() => { window.dispatchEvent(new CustomEvent(FLOOR_POST_CREATED_EVENT)); }} />
            </div>
          </div>
        </div>
      )}

      {collabNotice && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-ink/60 md:p-4" data-testid="collaboration-eligibility-overlay">
          <div role="alertdialog" aria-modal="true" aria-labelledby="collab-notice-title" aria-describedby="collab-notice-body"
            className="w-full md:max-w-md rounded-t-2xl md:rounded-2xl bg-paper p-5 shadow-xl" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom))" }}
            data-testid="collaboration-eligibility-notice">
            <div id="collab-notice-title" className="font-mont font-bold text-xs uppercase tracking-wide text-ink">{collabRetry ? "Couldn't check collaboration access" : "Collaborations aren't open for you yet"}</div>
            <p id="collab-notice-body" className="mt-2 text-sm text-ink-soft">{collabNotice}</p>
            {collabRetry && <button type="button" disabled={collabChecking} onClick={openCreateCollab} data-testid="collaboration-eligibility-retry"
              className="mt-4 w-full min-h-[44px] rounded-full bg-ink text-paper font-mont font-bold text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand">
              {collabChecking ? "Checking…" : "Retry"}
            </button>}
            <button type="button" autoFocus onClick={() => { collabRequestRef.current?.abort(); setCollabNotice(null); setTimeout(restoreFocus, 0); }} data-testid="collaboration-eligibility-dismiss"
              className="mt-4 w-full min-h-[44px] rounded-full bg-ink text-paper font-mont font-bold text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2">
              {collabRetry ? "Cancel" : "OK"}
            </button>
          </div>
        </div>
      )}

      {showCreateCollab && (
        <CreateCollaborationModal roleOptions={roleOptions} cityOptions={cityOptions}
          onClose={() => { setShowCreateCollab(false); setTimeout(restoreFocus, 0); }}
          onSaved={() => { setShowCreateCollab(false); setTimeout(restoreFocus, 0); window.dispatchEvent(new CustomEvent(FLOOR_POST_CREATED_EVENT)); }} />
      )}
    </div>
  );
}
